import { RightPanelTabName, SidePanelTabName } from '@mobile-mapping/providers';
import { t } from '@trimble/i18n';
import type { ControlMenuItemType } from '@trimble/ui-components/solidjs';
import { Accessor, createMemo, JSX } from 'solid-js';
import type { MainToolbarEntry } from './MainToolbar';
import { MainToolbarVisibilityControl } from '../components/MainToolbarVisibilityControl';
import { MAIN_TOOLBAR_SNAPPING_STORAGE_KEY } from './useMainToolbarSnapping';
import { useCadEditingToolbarItems } from './useCadEditingToolbarItems';
import type { UseGlobalToolbarItemsOptions } from './useGlobalToolbarItems.types';

const wrapToolStartItems = (
  items: ControlMenuItemType[],
  beforeStart: () => void,
  shouldWrapAction: (item: ControlMenuItemType) => boolean = () => true
): ControlMenuItemType[] =>
  items.map((item) => {
    if (item.type === 'action') {
      const children = item.children
        ? wrapToolStartItems(item.children, beforeStart, shouldWrapAction)
        : undefined;
      if (!shouldWrapAction(item)) {
        return { ...item, children };
      }
      return {
        ...item,
        onClick: (): void => {
          beforeStart();
          item.onClick();
        },
        children,
      };
    }

    if (item.type === 'radiogroup') {
      return {
        ...item,
        setValue: (value: string): void => {
          beforeStart();
          item.setValue(value);
        },
      };
    }

    if (item.type === 'checkbox' && item.children) {
      return {
        ...item,
        children: wrapToolStartItems(
          item.children,
          beforeStart,
          shouldWrapAction
        ),
      };
    }

    return item;
  });

/**
 * @intent Assembles the ordered list of main toolbar entries from
 * individual concerns (views, topics, measurements, markups, clipping,
 * semi-automatic detection, CAD editing, snapping, visibility, layout,
 * and settings) so
 * MainWindow only renders the result.
 */
export const useGlobalToolbarItems = (
  options: UseGlobalToolbarItemsOptions
): Accessor<MainToolbarEntry[]> => {
  const {
    selectedRightPanelTab,
    setSelectedRightPanelTab,
    setSelectedSidePanelTab,
    createTopicRequestId,
    setCreateTopicRequestId,
    setCreateTopicRequest,
    measurements,
    markups,
    clipping,
    snapping,
    semiAutomaticDetection,
    isLineDetectionEnabled = () => false,
    featureExtraction,
    isCadEditingEligible,
  } = options;

  // Stable JSX outside memo — avoid accumulating Solid computations on measurement state updates
  const visibilityControl: JSX.Element = <MainToolbarVisibilityControl />;
  const cadEditingToolbarItems = useCadEditingToolbarItems(
    featureExtraction,
    isCadEditingEligible
  );

  const toolbarItems = createMemo<MainToolbarEntry[]>(() => {
    const items: MainToolbarEntry[] = [
      {
        id: 'create-view',
        type: 'button',
        iconName: 'tc-icon-views-add',
        ariaLabel: t('mobileMapping.views.create.title'),
        tooltip: t('mobileMapping.views.create.title'),
        action: () => setSelectedRightPanelTab(RightPanelTabName.CreateView),
      },
      {
        id: 'create-topic',
        type: 'button',
        iconName: 'svg-bcf-add',
        ariaLabel: t('mobileMapping.globalToolbar.createTopic'),
        tooltip: t('mobileMapping.globalToolbar.createTopic'),
        action: () => {
          const id = createTopicRequestId() + 1;
          setCreateTopicRequestId(id);
          setSelectedSidePanelTab(SidePanelTabName.IssueManagement);
          setCreateTopicRequest({ type: 'create-topic', id });
        },
      },
      {
        type: 'split-dropdown',
        id: 'measurement',
        anchorIcon: 'tc-icon-measure',
        tooltip: t('mobileMapping.globalToolbar.measurement'),
        items: wrapToolStartItems(measurements.measurementMenuItems(), () => {
          markups.deactivateMarkup();
          if (isLineDetectionEnabled()) {
            semiAutomaticDetection.deactivateAllSemiAutomaticDetection();
          }
        }),
        pressed: measurements.isAnyMeasurementActive(),
        isAnchorActive: measurements.isAnyMeasurementActive(),
        onAnchorActiveClick: () => measurements.deactivateAllMeasurements(),
        storageKey: 'main-toolbar-measurement-last-tool',
      },
      {
        type: 'split-dropdown',
        id: 'markups',
        anchorIcon: markups.activeMarkupIcon(),
        tooltip: t('mobileMapping.globalToolbar.markups'),
        items: wrapToolStartItems(
          markups.markupMenuItems(),
          () => {
            measurements.deactivateAllMeasurements();
            if (isLineDetectionEnabled()) {
              semiAutomaticDetection.deactivateAllSemiAutomaticDetection();
            }
          },
          (item) => item.type === 'action' && item.replayable !== false
        ),
        pressed: markups.isMarkupActive(),
        isAnchorActive: markups.isMarkupActive(),
        onAnchorActiveClick: () => markups.deactivateMarkup(),
        storageKey: 'main-toolbar-markups-last-tool',
      },
      {
        type: 'split-dropdown',
        id: 'clip-planes',
        anchorIcon: 'svg-clip-plane',
        tooltip: t('mobileMapping.globalToolbar.clipPlanes'),
        items: wrapToolStartItems(
          clipping.clipMenuItems(),
          () => {
            measurements.deactivateAllMeasurements();
            markups.deactivateMarkup();
            if (isLineDetectionEnabled()) {
              semiAutomaticDetection.deactivateAllSemiAutomaticDetection();
            }
          },
          (item) => item.type === 'action' && item.replayable !== false
        ),
        pressed: clipping.isAnyClippingActive(),
        isAnchorActive: clipping.isAnyClippingActive(),
        onAnchorActiveClick: () => clipping.deactivateAllClipping(),
        storageKey: 'main-toolbar-clip-planes-last-tool',
      },
    ];

    if (isLineDetectionEnabled()) {
      items.push({
        type: 'split-dropdown',
        id: 'semi-automatic-detection',
        anchorIcon: 'tc-icon-line',
        tooltip: t('mobileMapping.globalToolbar.semiAutomaticDetection'),
        items: wrapToolStartItems(
          semiAutomaticDetection.semiAutomaticDetectionMenuItems(),
          () => {
            measurements.deactivateAllMeasurements();
            markups.deactivateMarkup();
          }
        ),
        pressed: semiAutomaticDetection.isAnySemiAutomaticDetectionActive(),
        isAnchorActive:
          semiAutomaticDetection.isAnySemiAutomaticDetectionActive(),
        onAnchorActiveClick: () =>
          semiAutomaticDetection.deactivateAllSemiAutomaticDetection(),
        storageKey: 'main-toolbar-semi-automatic-detection-last-tool',
      });
    }

    items.push(...cadEditingToolbarItems());

    items.push(
      { type: 'divider', id: 'main-toolbar-divider' },
      {
        type: 'split-dropdown',
        id: 'snapping',
        anchorIcon: 'tc-icon-snap-endpoint',
        tooltip: t('mobileMapping.globalToolbar.snapping'),
        items: snapping.snappingMenuItems(),
        pressed: false,
        storageKey: MAIN_TOOLBAR_SNAPPING_STORAGE_KEY,
        multipleSelection: true,
      },
      {
        type: 'custom',
        id: 'visibility',
        content: visibilityControl,
      },
      {
        id: 'layout',
        type: 'button',
        iconName: 'tc-icon-columns',
        ariaLabel: t('mobileMapping.layout.title'),
        tooltip: t('mobileMapping.layout.title'),
        pressed: selectedRightPanelTab() === RightPanelTabName.Layout,
        action: () =>
          setSelectedRightPanelTab(
            selectedRightPanelTab() === RightPanelTabName.Layout
              ? undefined
              : RightPanelTabName.Layout
          ),
      },
      {
        id: 'settings',
        type: 'button',
        iconName: 'tc-icon-settings',
        ariaLabel: t('mobileMapping.settings.title'),
        tooltip: t('mobileMapping.settings.title'),
        pressed: selectedRightPanelTab() === RightPanelTabName.Settings,
        action: () =>
          setSelectedRightPanelTab(
            selectedRightPanelTab() === RightPanelTabName.Settings
              ? undefined
              : RightPanelTabName.Settings
          ),
      }
    );

    return items;
  });

  return toolbarItems;
};
